import { CANDIDATE_TABS } from '~/common/constants/tab.constant'
import { shellPath, v2BaseFor } from '~/common/constants/v2-hosted-path'

export const PATHS = {
	// COMMON
	common: {
		home: '/',
		reset: '/reset',
		signin: '/signin',
		errors: '/errors',
		change_password: '/change_password',
		api: '/maestro/api/v1',
		public_graphql: '/api/graphql/public',
		// Timecards and pay live on the main app's candidate schema; the Maestro
		// engine has no types for them. See apollo.client.js.
		candidate_graphql: '/api/graphql/candidate',
		// CSV exports (exportCsvFile / exportFile) are the main app's recruiter schema, not the engine's.
		host_recruiter_graphql: '/api/graphql/recruiter',
		graphql: '/graphql',
		recruiter: '/recruiter',
		signup: '/signup',
		tracking: '/tracking',
		sso_authentication: '/authentication',
		unsubscribe_emails: '/unsubscribe_emails',
		auto_submission_feedback: '/auto_submission_confirmation',
	},
	// Recruiter Portal
	recruiter: {
		home: '/home',
		role: '/role',
		candidate_access: '/candidate-access',
		user_info: '/user_info',
		view_as_candidate: '/view_as_candidate',
		candidates: '/candidates',
		find_job: '/find_job',
		profile: '/profile',
		user_profile: '/user_profile',
		my_jobs: '/my_jobs',
		activation: '/activation',
		timecards: '/timecards',
		pay_matrix: '/pay_matrix',
		timecard_approval: '/timecard_approval',
		popular_cities: '/popular_cities',
		suggested_jobs: '/suggested_jobs',
		recruiter_jobs: '/recruiter_jobs',
		v2: '/v2',
		search: '/search',
		explore_matches: '/page/explore_matches',
		trending_jobs: '/page/trending_jobs',
		urgent_needs: '/page/urgent_needs',
		last_viewed: '/page/last_viewed',
		assigments: '/assigments',
		report: '/report',
		// SPT-2326: where applications come from (UTM / creator links), one page.
		applicant_sources: '/applicant-sources',
		jobs: '/jobs',
		toas: '/toas',
		job_info: '/job_info',
		candidate_info: '/candidate_info',
		job_applicants: '/job_applicants',
		notification: '/notification',
		messages: '/messages',
		cadences: '/cadences',
		cadence_templates: '/email-templates',
		// Smart communication S4: candidates who acted on a workflow message. Its own route (not under /cadences, where
		// every segment is a workflow id).
		cadence_hot_candidates: '/hot-candidates',
		// Smart communication S3: saved audiences for workflows, also outside /cadences.
		cadence_segments: '/workflow-segments',
		configuration: '/configuration',
		// Notification settings hub (Configuration › Notifications). Top level like the other configuration
		// screens (routerStore.basePath is the first segment); not to be confused with the bell's '/notification'.
		notifications: '/notifications',
		// The recruiter's own job-area alerts (SPT-2331): '/job-alerts', '/job-alerts/new', '/job-alerts/<id>/edit'.
		job_alerts: '/job-alerts',
		candidates_assignments: `/candidates/${CANDIDATE_TABS.on_assignment}`,
		candidates_submission: `/candidates/${CANDIDATE_TABS.submission}`,
		candidates_matches: `/candidates/${CANDIDATE_TABS.matches}`,
		candidates_pre_submissions: `/candidates/${CANDIDATE_TABS.pre_submission}`,
		candidates_submissions_ready: `/candidates/${CANDIDATE_TABS.submission_ready}`,
		candidates_shortlist: `/candidates/${CANDIDATE_TABS.shortlist}`,
		candidates_dismissed: `/candidates/${CANDIDATE_TABS.dismissed}`,
	},
}

// Keep cadence URLs in one place. These are functions rather than interpolated
// strings at call sites so a route can never accidentally contain an unescaped
// API identifier.
export const cadenceDetailPath = (id, pathname) => shellPath(`${PATHS.recruiter.cadences}/${encodeURIComponent(id)}`, pathname)
// The builder page has three tabs: the workflow itself (no suffix), who is enrolled in it, and how it performs.
export const CADENCE_DETAIL_TABS = ['workflow', 'candidates', 'performance']
export const cadenceCandidatesPath = (id, pathname) => `${cadenceDetailPath(id, pathname)}/candidates`
export const cadencePerformancePath = (id, pathname) => `${cadenceDetailPath(id, pathname)}/performance`
export const cadenceNewPath = (pathname) => shellPath(`${PATHS.recruiter.cadences}/new`, pathname)
export const cadenceNewAiPath = (pathname) => shellPath(`${PATHS.recruiter.cadences}/new/ai`, pathname)
export const cadenceTemplateDetailPath = (id, pathname) => shellPath(`${PATHS.recruiter.cadence_templates}/${encodeURIComponent(id)}`, pathname)
export const cadenceTemplateNewPath = (pathname) => shellPath(`${PATHS.recruiter.cadence_templates}/new`, pathname)

export const cadenceRouteTarget = (pathname, basePath) => {
	const base = v2BaseFor(basePath, pathname)
	if (pathname === base || pathname === `${base}/`) return { type: 'list' }
	if (!pathname.startsWith(`${base}/`)) return { type: 'invalid' }
	const [segment, tab, ...rest] = pathname.slice(base.length + 1).split('/')
	if (!segment || rest.length) return { type: 'invalid' }
	if (segment === 'new') {
		if (tab === undefined) return { type: 'new' }
		return tab === 'ai' ? { type: 'newAi' } : { type: 'invalid' }
	}
	// `/<id>` is the workflow tab; only `/<id>/candidates` and `/<id>/performance` are other tabs (a trailing slash is not).
	if (tab !== undefined && !CADENCE_DETAIL_TABS.slice(1).includes(tab)) return { type: 'invalid' }
	try {
		return { type: 'detail', id: decodeURIComponent(segment), tab: tab || CADENCE_DETAIL_TABS[0] }
	} catch (_error) {
		return { type: 'invalid' }
	}
}

export const notificationDetailPath = (id, pathname) => shellPath(`${PATHS.recruiter.notifications}/${encodeURIComponent(id)}`, pathname)

// '/notifications' is the list (and its Activity tab), '/notifications/<id>' one notification's editor.
// Inside V2 the same screens live under /v2/configuration/notifications.
export const notificationRouteTarget = (pathname) => {
	const base = v2BaseFor(PATHS.recruiter.notifications, pathname)
	if (pathname === base || pathname === `${base}/`) return { type: 'list' }
	if (!pathname.startsWith(`${base}/`)) return { type: 'invalid' }
	const [segment, ...rest] = pathname.slice(base.length + 1).split('/')
	if (!segment || rest.length) return { type: 'invalid' }
	try {
		return { type: 'detail', id: decodeURIComponent(segment) }
	} catch (_error) {
		return { type: 'invalid' }
	}
}
